import React, { useEffect, useState } from 'react';
import './Timeline.css';
import { Tooltip, IconButton } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import EditScheduleModal from './EditScheduleModal';
import { Droppable } from '@hello-pangea/dnd';

type ScheduledTask = {
    id: string;
    title: string;
    startTime: number;
    endTime: number;
    description?: string;
    estimatedLength?: number;
};

interface TimelineProps {
    startHour: number;
    endHour: number;
    tasks: ScheduledTask[];
    onUpdateTasks: (updatedTasks: ScheduledTask[]) => void;
}

const formatTime = (time: number) => {
    const hours = Math.floor(time);
    const minutes = Math.round((time - hours) * 60);
    return `${hours}:${minutes === 0 ? '00' : minutes}`;
};

const getCurrentTimeInHours = () => {
    const now = new Date();
    return now.getHours() + now.getMinutes() / 60;
};

const taskColors = ['#ffb3ba', '#baffc9', '#bae1ff', '#ffffba', '#E7B3FF'];

const Timeline: React.FC<TimelineProps> = ({ startHour, endHour, tasks, onUpdateTasks }) => {
    const [currentTime, setCurrentTime] = useState(getCurrentTimeInHours());
    const [isModalOpen, setIsModalOpen] = useState(false);

    useEffect(() => {
        const interval = setInterval(() => {
            setCurrentTime(getCurrentTimeInHours());
        }, 60000); // Update every minute

        return () => clearInterval(interval);
    }, []);

    const handleOpenModal = () => {
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
    };

    const handleSave = (updatedTasks: ScheduledTask[]) => {
        onUpdateTasks(updatedTasks);
    };

    const hours = Array.from({ length: endHour - startHour + 1 }, (_, i) => startHour + i);

    return (
        <div className="timeline-card">
            <div className="timeline-header">
                <h2>Today's Timeline</h2>
                <IconButton className="edit-icon" onClick={handleOpenModal}>
                    <EditIcon />
                </IconButton>
            </div>
            <div className="timeline-current-time" style={{ left: `${((currentTime - startHour) / (endHour - startHour + 1)) * 100}%` }} />
            <Droppable droppableId="timeline" direction="horizontal">
                {(provided) => (
                    <div className="timeline-tasks" ref={provided.innerRef} {...provided.droppableProps}>
                        {tasks.map((task, index) => {
                            const taskDuration = task.endTime - task.startTime;
                            const taskColor = taskColors[index % taskColors.length];
                            return (
                                <Tooltip
                                    key={task.id}
                                    title={
                                        <div className="task-tooltip">
                                            <h3>{task.title}</h3>
                                            <p>{task.description}</p>
                                            <p>{`Start: ${formatTime(task.startTime)}`}</p>
                                            <p>{`End: ${formatTime(task.endTime)}`}</p>
                                        </div>
                                    }
                                    arrow
                                >
                                    <div
                                        className="timeline-task"
                                        style={{
                                            left: `${((task.startTime - startHour) / (endHour - startHour + 1)) * 100}%`,
                                            width: `calc(${(taskDuration / (endHour - startHour + 1)) * 100}% - 24px)`, // Adjust width to account for padding and margin
                                            backgroundColor: taskColor,
                                        }}
                                    >
                                        {taskDuration >= 1 ? task.title : ''}
                                    </div>
                                </Tooltip>
                            );
                        })}
                        {provided.placeholder}
                    </div>
                )}
            </Droppable>
            <div className="timeline-content">
                {hours.map((hour) => (
                    <div key={hour} className="timeline-hour">
                        {hour}:00
                    </div>
                ))}
            </div>
            <EditScheduleModal
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                onSave={handleSave}
                tasks={tasks}
            />
        </div>
    );
};

export default Timeline;
export type { ScheduledTask };